import type { ReactNode } from 'react';

/**
 * Renders the small markdown subset agents reply with: paragraphs, headings, bullet and numbered
 * lists, **bold**, *italic* and `code`. It builds React elements only (never raw HTML), so agent
 * text cannot inject markup.
 */
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g;

const renderInline = (text: string): ReactNode[] =>
  text.split(INLINE).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={index} className="font-semibold text-neutral-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code
          key={index}
          className="rounded bg-neutral-100 px-1 py-px font-mono text-[12px] text-neutral-800"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    return part;
  });

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'ul' | 'ol'; items: string[] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^\s{0,3}#{1,6}\s+(.*)$/;

const parseBlocks = (source: string): Block[] => {
  const blocks: Block[] = [];
  for (const line of source.replace(/\r\n/g, '\n').split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) {
      blocks.push({ kind: 'paragraph', lines: [] });
      continue;
    }
    const last = blocks[blocks.length - 1];
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);

    if (heading) {
      blocks.push({ kind: 'heading', text: heading[1] });
    } else if (bullet) {
      if (last?.kind === 'ul') last.items.push(bullet[1]);
      else blocks.push({ kind: 'ul', items: [bullet[1]] });
    } else if (numbered) {
      if (last?.kind === 'ol') last.items.push(numbered[1]);
      else blocks.push({ kind: 'ol', items: [numbered[1]] });
    } else if (last?.kind === 'paragraph') {
      // A blank line leaves an empty paragraph behind, so the next text starts a new one.
      last.lines.push(trimmed);
    } else {
      blocks.push({ kind: 'paragraph', lines: [trimmed] });
    }
  }
  return blocks.filter((block) => block.kind !== 'paragraph' || block.lines.length > 0);
};

export const SegmentAiMarkdown = ({ text, className = '' }: { text: string; className?: string }) => (
  <div className={`space-y-2 text-[13px] leading-5 text-neutral-700 ${className}`}>
    {parseBlocks(text).map((block, index) => {
      if (block.kind === 'heading') {
        return (
          <p key={index} className="pt-1 text-[13px] font-semibold text-neutral-900">
            {renderInline(block.text)}
          </p>
        );
      }
      if (block.kind === 'ul' || block.kind === 'ol') {
        const List = block.kind === 'ul' ? 'ul' : 'ol';
        return (
          <List
            key={index}
            className={`space-y-1 pl-5 ${block.kind === 'ul' ? 'list-disc' : 'list-decimal'}`}
          >
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex} className="pl-0.5">
                {renderInline(item)}
              </li>
            ))}
          </List>
        );
      }
      return <p key={index}>{renderInline(block.lines.join(' '))}</p>;
    })}
  </div>
);
